import { useEffect, useRef, useState } from "react";
import { CheckCircle2, FileUp, Loader2, Plus, ShieldAlert, ShieldCheck, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { isEmail } from "@/lib/validate";
import { useFormGuard } from "@/lib/formGuard";

const MAX = 15 * 1024 * 1024;
const MAX_FILES = 10;

function SignatureResult({ result, u, compact }) {
  if (result.signed) {
    return (
      <div data-testid="upload-signed" className={`rounded-xl border border-brand-teal/40 bg-brand-teal/5 flex gap-3 ${compact ? "p-3" : "p-4"}`}>
        <ShieldCheck size={18} className="text-brand-teal shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-brand-teal">{u.signed}</p>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            {result.signatures.map((s, i) => (
              <li key={i} data-testid={`upload-signature-${i}`}>
                {u.signed_by} <span className="font-medium text-foreground">{s.name || u.unknown_signer}</span>
                {s.issuer && <span> · {s.issuer}</span>}{s.time && <span> · {s.time}</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="upload-unsigned" className={`rounded-xl border border-brand-red/40 bg-brand-red/5 flex gap-3 ${compact ? "p-3" : "p-4"}`}>
      <ShieldAlert size={18} className="text-brand-red shrink-0 mt-0.5" />
      <div className="text-sm">
        <p className="font-semibold text-brand-red">{u.unsigned}</p>
        <p className="text-muted-foreground mt-1 leading-relaxed">
          {u.unsigned_hint}{" "}
          <a data-testid="upload-guide-link" href="/biuro-wirtualne-warszawa/dokumenty#biuro-wirtualne-poradnik" className="font-semibold text-brand-teal underline underline-offset-2">{u.guide}</a>
        </p>
      </div>
    </div>
  );
}

function FileRow({ item, index, onRemove, u }) {
  return (
    <div data-testid={`upload-file-${index}`} className="space-y-2">
      <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3">
        <FileUp size={18} className="text-brand-teal shrink-0" />
        <div className="flex-1 min-w-0">
          <p data-testid={`upload-filename-${index}`} className="text-sm font-semibold truncate">{item.file.name}</p>
          <p className="text-xs text-muted-foreground">{(item.file.size / 1024).toFixed(0)} KB</p>
        </div>
        {item.verifying && <Loader2 size={15} className="animate-spin text-muted-foreground" />}
        <button type="button" data-testid={`upload-remove-button-${index}`} onClick={onRemove} aria-label="Usuń" className="p-1.5 rounded-full hover:bg-muted"><X size={15} /></button>
      </div>
      {item.verifying && <p data-testid="upload-verifying" className="text-xs text-muted-foreground inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> {u.verifying}</p>}
      {item.result && <SignatureResult result={item.result} u={u} compact />}
    </div>
  );
}

export default function UploadBox({ docName = "", compact = false, orderId = "" }) {
  const { t, lang } = useT();
  const u = t.upload;
  const inputRef = useRef(null);
  const { appendTo, Honeypot, GuardNotice } = useFormGuard();
  const [items, setItems] = useState([]);
  const [drag, setDrag] = useState(false);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [note, setNote] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(0);
  const [err, setErr] = useState({});
  const [expected, setExpected] = useState(1);

  useEffect(() => {
    if (!orderId) return;
    api.get("/uploads/signers", { params: { order: orderId } }).then((r) => setExpected(r.data.expected || 1)).catch(() => {});
  }, [orderId]);

  const patch = (id, data) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...data } : it)));

  const addFiles = async (list) => {
    const incoming = Array.from(list || []).filter(Boolean);
    if (!incoming.length) return;
    const accepted = [];
    for (const f of incoming) {
      if (items.length + accepted.length >= MAX_FILES) { toast.error(u.max_files.replace("{n}", String(MAX_FILES))); break; }
      if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) { toast.error(`${f.name}: ${u.not_pdf}`); continue; }
      if (f.size > MAX) { toast.error(`${f.name}: ${u.too_big}`); continue; }
      if (items.some((it) => it.file.name === f.name && it.file.size === f.size) || accepted.some((it) => it.file.name === f.name && it.file.size === f.size)) { toast.error(u.duplicate.replace("{name}", f.name)); continue; }
      accepted.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file: f, result: null, verifying: true });
    }
    if (!accepted.length) return;
    setItems((prev) => [...prev, ...accepted]);
    await Promise.all(accepted.map(async (it) => {
      try {
        const fd = new FormData();
        fd.append("file", it.file);
        const r = await api.post("/uploads/verify", fd);
        patch(it.id, { result: r.data, verifying: false });
      } catch (e) {
        const d = e.response?.data?.detail;
        toast.error(`${it.file.name}: ${e.response?.status === 429 ? t.errors.too_many : typeof d === "string" ? d : u.err}`);
        setItems((prev) => prev.filter((x) => x.id !== it.id));
      }
    }));
  };

  const remove = (id) => setItems((prev) => prev.filter((it) => it.id !== id));
  const reset = () => { setItems([]); setDone(0); setErr({}); };

  const allSigned = items.length > 0 && items.every((it) => it.result?.signed);
  const anyVerifying = items.some((it) => it.verifying);
  const hasUnsigned = items.some((it) => it.result && !it.result.signed);
  const detectedMax = items.reduce((m, it) => Math.max(m, it.result?.count || 0), 0);
  const sigShort = expected > 1 && allSigned && detectedMax < expected;

  const send = async (e) => {
    e.preventDefault();
    const er = {};
    if (!isEmail(email)) er.email = t.errors.email;
    if (!consent) er.consent = t.errors.consent;
    setErr(er);
    if (Object.keys(er).length) return;
    if (!allSigned) return toast.error(u.remove_unsigned);
    setSending(true);
    try {
      const fd = new FormData();
      items.forEach((it) => fd.append("files", it.file));
      fd.append("email", email);
      fd.append("company", company);
      fd.append("note", note);
      fd.append("doc_name", docName);
      if (orderId) fd.append("order_id", orderId);
      fd.append("lang", lang === "pl" ? "pl" : "en");
      await appendTo(fd);
      await api.post("/uploads", fd);
      setDone(items.length);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      if (d?.code === "unsigned") {
        setItems((prev) => prev.map((it) => (it.file.name === d.filename ? { ...it, result: { signed: false, signatures: [] } } : it)));
        toast.error(u.remove_unsigned);
      } else toast.error(e2.response?.status === 429 ? t.errors.too_many : typeof d === "string" ? d : u.err);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="upload-success" className="rounded-2xl border border-brand-teal/40 bg-brand-teal/5 p-6 text-center">
        <CheckCircle2 size={36} className="mx-auto text-brand-teal" />
        <h3 className="font-display font-bold text-lg mt-3">{done > 1 ? u.success_title_multi.replace("{n}", String(done)) : u.success_title}</h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{u.success_sub.replace("{email}", email)}</p>
        <button type="button" data-testid="upload-another-button" onClick={reset} className="mt-4 text-xs font-semibold text-brand-teal hover:underline">{u.another}</button>
      </div>
    );
  }

  const dropzone = (big) => (
    <button
      type="button"
      data-testid={big ? "upload-dropzone" : "upload-add-more"}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
      className={`w-full rounded-2xl border-2 border-dashed text-center transition-colors ${big ? "p-8" : "p-4"} ${drag ? "border-brand-teal bg-brand-teal/5" : "border-border hover:border-brand-teal/60 hover:bg-muted/40"}`}
    >
      {big ? (
        <>
          <FileUp size={28} className="mx-auto text-brand-teal" />
          <p className="font-semibold text-sm mt-3">{u.drop}</p>
          <p className="text-xs text-muted-foreground mt-1">{u.drop_hint} {u.multi_hint.replace("{n}", String(MAX_FILES))}</p>
        </>
      ) : (
        <p className="font-semibold text-sm inline-flex items-center gap-2 text-brand-teal"><Plus size={16} /> {u.add_more} <span className="text-xs text-muted-foreground font-normal">({items.length}/{MAX_FILES})</span></p>
      )}
    </button>
  );

  return (
    <div data-testid="upload-box" className={compact ? "" : "space-y-4"}>
      <input ref={inputRef} data-testid="upload-file-input" type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      {expected > 1 && (
        <div data-testid="upload-sig-expected-info" className="mb-4 rounded-xl border border-brand-teal/40 bg-brand-teal/5 px-4 py-3 text-xs leading-relaxed flex gap-2.5">
          <ShieldCheck size={16} className="text-brand-teal shrink-0 mt-0.5" />
          <span>{u.sig_expected.replace("{n}", String(expected))}</span>
        </div>
      )}
      {!items.length ? dropzone(true) : (
        <form onSubmit={send} className="space-y-4" noValidate>
          <Honeypot />
          <div data-testid="upload-file-list" className="space-y-3">
            {items.map((it, i) => <FileRow key={it.id} item={it} index={i} u={u} onRemove={() => remove(it.id)} />)}
          </div>
          {items.length < MAX_FILES && dropzone(false)}
          {hasUnsigned && <p data-testid="upload-has-unsigned" className="text-xs text-brand-red font-semibold">{u.remove_unsigned}</p>}
          {sigShort && (
            <div data-testid="upload-sig-count-warning" className="rounded-xl border border-brand-yellow/60 bg-brand-yellow/10 px-4 py-3 text-xs leading-relaxed flex gap-2.5">
              <ShieldAlert size={16} className="text-brand-yellow shrink-0 mt-0.5" />
              <span>{u.sig_count_warning.replace("{expected}", String(expected)).replace("{detected}", String(detectedMax))}</span>
            </div>
          )}
          {allSigned && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{u.email} *</label>
                  <Input data-testid="upload-email-input" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErr({ ...err, email: undefined }); }} placeholder={t.docs.email_ph} className={`bg-background ${err.email ? "border-rose-500" : ""}`} />
                  {err.email && <p data-testid="upload-email-error" className="text-xs text-rose-600 mt-1">{err.email}</p>}
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{u.company}</label>
                  <Input data-testid="upload-company-input" value={company} onChange={(e) => setCompany(e.target.value)} className="bg-background" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{u.note}</label>
                <Textarea data-testid="upload-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="bg-background resize-none" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer text-xs text-muted-foreground leading-relaxed">
                <Checkbox data-testid="upload-consent-checkbox" checked={consent} onCheckedChange={(v) => { setConsent(v); setErr({ ...err, consent: undefined }); }} className={`mt-0.5 ${err.consent ? "border-rose-500" : ""}`} />
                <span>{t.order.consent} <a href="/biuro-wirtualne-warszawa/rodo" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 font-semibold text-foreground">{t.order.consent_link}</a>.</span>
              </label>
              {err.consent && <p data-testid="upload-consent-error" className="text-xs text-rose-600 -mt-2">{err.consent}</p>}
              <GuardNotice />
              <button type="submit" data-testid="upload-submit-button" disabled={sending || anyVerifying} className="w-full sm:w-auto bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-8 py-3 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {sending ? u.sending : items.length > 1 ? u.send_multi.replace("{n}", String(items.length)) : u.send}
              </button>
            </>
          )}
        </form>
      )}
    </div>
  );
}
